/** Verify a complete Angular app release over the network before retiring a
 * stuck worker. Warm the HTTP cache without deleting the working offline copy.
 */
export async function verifyPlayerRelease(
  fetchResource: typeof fetch,
  origin: string,
  version: string,
  signal: AbortSignal,
): Promise<boolean> {
  const request = (path: string) =>
    fetchResource(new URL(path, origin).href, {
      cache: "reload",
      headers: { "ngsw-bypass": "true" },
      signal,
    });
  const versionMatches = async () => {
    const response = await request("/assets/VERSION.txt");
    return response.ok && (await response.text()).trim() === version;
  };
  if (!(await versionMatches())) return false;
  const response = await request("/ngsw.json");
  if (!response.ok) return false;
  const manifestText = await response.text();
  const manifest = JSON.parse(manifestText);
  if (
    !Array.isArray(manifest.assetGroups) ||
    typeof manifest.hashTable !== "object" ||
    !manifest.hashTable
  )
    return false;
  const urls: unknown[] = manifest.assetGroups
    .filter((group: { installMode?: string }) => group.installMode === "prefetch")
    .flatMap((group: { urls?: unknown[] }) => (Array.isArray(group.urls) ? group.urls : []));
  if (
    !urls.includes("/index.html") ||
    !urls.some((url) => typeof url === "string" && url.endsWith(".js"))
  )
    return false;
  const available = await Promise.all(
    urls.map(async (path) => {
      if (typeof path !== "string" || !path.startsWith("/") || path.startsWith("//")) return false;
      const expected = manifest.hashTable[path];
      if (typeof expected !== "string" || !/^[a-f0-9]{40}$/.test(expected)) return false;
      const resource = await request(path).catch((error) => {
        throw new Error(`Unable to fetch player asset ${path}`, { cause: error });
      });
      if (!resource.ok) return false;
      const bytes = await resource.arrayBuffer();
      const digest = await crypto.subtle.digest("SHA-1", bytes);
      const actual = [...new Uint8Array(digest)]
        .map((byte) => byte.toString(16).padStart(2, "0"))
        .join("");
      return actual === expected;
    }),
  );
  if (!available.every(Boolean) || !(await versionMatches())) return false;
  // A rolling deployment must not mix the manifest and assets of two releases.
  const currentManifest = await request("/ngsw.json");
  return currentManifest.ok && (await currentManifest.text()) === manifestText;
}
